import { useState } from "react";
import { AlertTriangleIcon, UnlockIcon, XIcon } from "lucide-react";
import type { PayrollUiPayPeriod } from "../../types";
interface UnlockPeriodModalProps {
  isOpen: boolean;
  onClose: () => void;
  period: PayrollUiPayPeriod;
  onConfirm: (reason: string) => void;
}
export function UnlockPeriodModal({
  isOpen,
  onClose,
  period,
  onConfirm
}: UnlockPeriodModalProps) {
  const [reason, setReason] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  if (!isOpen) return null;
  const isValid = reason.length >= 20 && acknowledged;
  const handleConfirm = () => {
    if (isValid) {
      onConfirm(reason);
      onClose();
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-sm"
        onClick={onClose} />
      
      <div className="relative w-full max-w-[600px] bg-surface-light rounded-[12px] shadow-xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between p-5 border-b border-border bg-warning-background/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-warning-background flex items-center justify-center">
              <AlertTriangleIcon className="w-5 h-5 text-coral" />
            </div>
            <div>
              <h2 className="font-heading text-lg font-semibold text-iron-grey">
                Unlock Pay Period
              </h2>
              <p className="font-body text-sm text-text-secondary mt-0.5">
                {period.label}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-iron-grey transition-colors">
            
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-5">
          <div className="bg-warning-background border-l-4 border-coral rounded-r-[8px] p-4">
            <h3 className="font-body text-sm font-semibold text-iron-grey mb-2">
              Warning: This period has already been exported
            </h3>
            <p className="font-body text-sm text-text-secondary">
              Unlocking this period will allow you to make changes to data that
              has already been sent to your payroll provider. Any changes will
              require a new export and may need to be manually reconciled with
              your provider.
            </p>
          </div>

          <div>
            <label className="block font-body text-sm font-medium text-iron-grey mb-1.5">
              Reason for Unlocking <span className="text-strawberry">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              placeholder="Explain why this period needs to be unlocked..."
              data-testid="payroll-unlock-reason"
              className="w-full px-3 py-2 bg-surface-light border border-border rounded-[6px] font-body text-sm text-iron-grey focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary resize-none" />
            
            <p className="font-body text-xs text-text-secondary mt-1">
              {reason.length}/∞ characters (minimum 20 required)
            </p>
          </div>

          <label className="flex items-start gap-3 p-4 bg-muted rounded-[8px] cursor-pointer">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              data-testid="payroll-unlock-acknowledged"
              className="mt-0.5 rounded border-border text-primary focus:ring-primary w-4 h-4 cursor-pointer" />
            
            <span className="font-body text-sm text-iron-grey">
              I understand this period was previously exported and any changes
              will require a new export file
            </span>
          </label>
        </div>

        <div className="p-5 border-t border-border bg-background-light flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 font-body text-sm font-medium text-iron-grey bg-surface-light border border-border rounded-[6px] hover:bg-muted transition-colors">
            
            Cancel
          </button>
          <button
            disabled={!isValid}
            onClick={handleConfirm}
            data-testid="payroll-unlock-confirm"
            className="flex items-center gap-2 px-4 py-2 font-body text-sm font-medium text-white bg-coral rounded-[6px] hover:bg-strawberry transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
            
            <UnlockIcon className="w-4 h-4" />
            Unlock Period
          </button>
        </div>
      </div>
    </div>);

}
